import React, { useEffect } from "react";
import { IHotelCardPriceProps } from "./HotelCardPriceProps";
import { PriceContainer } from "./HotelCardPriceHelpers";
import { priceFormatter } from "../../../utils/helpers";
import OccupancyMessage from "../OccupancyMessage/OccupancyMessage";
import { ButtonWrapper } from "../HotelCard/HotelCardHelpers";
import UnlockPriceButton from "../UnlockPriceButton/UnlockPriceButton";
import {
  hotelRoomUnlockButtonWapper,
  memberRateLabel,
  unlockButton,
} from "../HotelCard/HotelCard.module.scss";
import { formatRedemptionItem } from "../../../utils/helpers";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useAppSelector } from "../../../hooks";
import { searchSelector } from "../../../redux/slices/Search/search";
import { fireMemberRatePresent } from "../../../utils/datalayers";
import InfoIconTooltip from "../InfoIconTooltip/InfoIconTooltip";

const HotelCardPrice: React.FC<IHotelCardPriceProps> = ({
  phone,
  price,
  baseRate,
  baseRateFees,
  feeItems,
  fromRateFeeItems,
  fromRateFees,
  locked,
  hotel,
  discountCode,
  display,
  showUnlockButton,
  showMemberRateLabel,
  isPromoValid,
  handleCheckAvailability,
  hotelRedemptionItem,
  showOccupancy,
}) => {
  const phoneNumber =
    phone && phone.length ? (
      <a
        href={`tel:${phone}`}
        className="hotelSearchPhone"
        data-hotelname={hotel.name}
      >
        {phone}
      </a>
    ) : null;

  const search = useAppSelector(searchSelector);
  const isLoggedIn = useAppSelector((state: any) => state.member?.isLoggedIn);

  const adults = search.rooms[0].adults;
  const children = search.rooms[0].children;
  const adultWord = "Adult";
  const childWord = "Child";
  const adultsText = `${adults} ${adultWord}${adults > 1 ? "s" : ""}`;
  const childrenText =
    children > 0
      ? `, ${children} ${childWord}${children > 1 ? "ren" : ""}`
      : "";

  const isMobile = useMediaQuery("(max-width: 991px)");
  let renderedPrice = null;
  const showPromoApplieddText = discountCode && hotel.IsPromotionValid;

  useEffect(() => {
    if (showMemberRateLabel || showUnlockButton) {
      fireMemberRatePresent("Search Results", hotel);
    }
  }, [showMemberRateLabel, showUnlockButton]);

  const getAppliedRateLabel = () => {
    const onlyTPRNR = hotel?.bestRateCode === "TPRNR";
    if (onlyTPRNR) return null;

    if (!discountCode) return null;
    const code = discountCode.toUpperCase();
    if (code === "GOV") {
      return "GOVERNMENT RATE";
    }
    if (code === "AAA") {
      return "AAA / SENIOR RATE";
    }
    if (code === "PKG") {
      return "PACKAGE RATE";
    }
    return null;
  };

  const appliedRateLabel = search.discount && getAppliedRateLabel();

  if (locked !== undefined) {
    renderedPrice = locked ? baseRate : price;
    if (renderedPrice) {
      const shouldShowStrikeThrough =
        (baseRate &&
          (showPromoApplieddText || !discountCode) &&
          !locked &&
          baseRate > price) ||
        (process.env.GATSBY_SHOW_MEMEBER_RATE_LABEL === "true" &&
          isPromoValid &&
          !hotel.IsPromotionValid &&
          showMemberRateLabel &&
          baseRate > price);

      const renderStrikeThrough = () => {
        if (!shouldShowStrikeThrough) return null;

        return (
          <del className="strikethrough">
            {priceFormatter(baseRate, 0, "USD")}
            <span className="visuallyhidden">Old Price</span>
          </del>
        );
      };

      const strikeThrough =
        process.env.GATSBY_SHOW_MEMEBER_RATE_LABEL === "true"
          ? isLoggedIn
            ? null
            : renderStrikeThrough()
          : renderStrikeThrough();

      const renderedFeeItems =
        feeItems?.length > 0
          ? feeItems
          : fromRateFeeItems?.length > 0
          ? fromRateFeeItems
          : null;
      return (
        <PriceContainer id={"hotel-room-price"}>
          <div className={isMobile ? "d-flex" : "promo-container"}>
            {showPromoApplieddText && (
              <div
                className={`promo-text d-block ${
                  display !== "map" ? "d-lg-block" : "d-lg-none"
                }`}
              >
                {discountCode.toUpperCase()} <span>Applied</span>
              </div>
            )}
          </div>
          <div className="price-div">
            <div
              className={`renderedPrice ${!isMobile ? "" : ""} ${
                display === "map" ? "" : ""
              }`}
            >
              {showPromoApplieddText && (
                <div
                  className={
                    display !== "map"
                      ? `promo-text d-none d-lg-none`
                      : `map-promo-text`
                  }
                >
                  {discountCode.toUpperCase()} <span>Applied</span>
                </div>
              )}
              <div className="searchPriceDiv">
                <div
                  className={`d-flex ${!isMobile ? "flex-sm-column" : ""} ${
                    strikeThrough ? "neg-margin" : "margin-price"
                  }`}
                >
                  <div className="searchPrice">
                    <div className="strikeThrough-wrapper">
                      {strikeThrough}{" "}
                    </div>
                    {priceFormatter(renderedPrice, 0, hotel.currency)}{" "}
                  </div>
                  <div>
                    {showOccupancy && (
                      <div className="occupany-text">
                        <div>
                          {adultsText}
                          {childrenText}
                        </div>
                      </div>
                    )}

                    <div
                      className={
                        isMobile ? "occupancy occupancy-mobile" : "occupancy"
                      }
                    >
                      <span
                        className="occupancy-div"
                        style={{ marginBottom: "6px", marginLeft: "1px" }}
                      >
                        <OccupancyMessage
                          page="search"
                          baseRateFees={baseRateFees}
                          fromRateFees={fromRateFees}
                          crsCode={hotel.crsHotelCode}
                          className={`from-text d-inline`}
                        />
                      </span>
                      {renderedFeeItems && renderedFeeItems?.length > 0 && (
                        <InfoIconTooltip content={renderedFeeItems} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {hotelRedemptionItem?.currencyRequired && isLoggedIn && (
              <div className="hotel-card-redemption-rate">
                <span>
                  or{" "}
                  {formatRedemptionItem(hotelRedemptionItem?.currencyRequired)}{" "}
                  points per night
                </span>
              </div>
            )}
            <div className={`px-lg-0 text-center SelectButtonWrapper`}>
              {price !== null && (
                <ButtonWrapper
                  onClick={handleCheckAvailability}
                  aria-label={`Select for ${hotel.name}`}
                  regularText={true}
                >
                  Select
                </ButtonWrapper>
              )}
              {showUnlockButton && (
                <div className={hotelRoomUnlockButtonWapper}>
                  <UnlockPriceButton unlockClass={unlockButton} />
                </div>
              )}
              {appliedRateLabel ? (
                <div className={memberRateLabel}>{appliedRateLabel}</div>
              ) : (
                showMemberRateLabel && (
                  <div className={memberRateLabel}>MEMBER RATE</div>
                )
              )}
            </div>
          </div>
        </PriceContainer>
      );
    } else if (!price) {
      return (
        <div
          className={`pb-lg-4 d-flex align-items-center h-100 mb-3 mb-lg-0 justify-content-start ${
            display !== "map" ? "justify-content-md-center" : ""
          }`}
        >
          {phoneNumber !== null ? (
            <>
              <div className={"callforavailability text-center"}>
                Call for Availability
                <div className="text-center" style={{ marginTop: "4px" }}>
                  {phoneNumber}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center">
              <PriceContainer>Sold Out</PriceContainer>
            </div>
          )}
        </div>
      );
    }
  }
  return null;
};
export default HotelCardPrice;
